import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { Field, Form, Formik } from 'formik';
import { AppUser } from 'Src/views/App/App';
import {
  getFullName,
  searchFunctionEventCoaches,
  searchFunctionEventMembersOfStaff
} from 'Src/helpers/autocomplete/autocomplete';
import { CLUB_STAFF } from 'Src/consts/club';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { getEventDetailsCoaches, getEventDetailsMembersOfStaff } from 'Src/helpers/event/event';
import { getSchoolEventCoaches, getSchoolEventStaff } from 'Src/helpers/service/admin/schoolEvents';
import { isClubEvent } from '../../../../../../helpers/event/event';
import { SchoolEvent } from '../../../../../../models/event';
import { HTMLEditorQuillHide } from '../../../../../../components/HTMLEditorQuill/HTMLEditorQuillHide';
import { CLUB_EVENT_EDIT_MODE } from '../../../../../../consts/event';
import { SchoolEventClubEventRadioButtons } from '../SchoolEventClubEventRadioButtons/SchoolEventClubEventRadioButtons';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import './SchoolEventDetailsForm.scss';

interface Props {
  user: AppUser;
  eventId: string;
  eventsDetails: any;
  onCancel: () => void;
  onSubmit: (data: any, editMode: string) => void;
  event: SchoolEvent;
}

interface State {
  membersOfStaff: any[];
  coaches: any[];
  isChangeFollowingEvents: boolean;
  editMode: string;
}

export class SchoolEventDetailsForm extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      coaches: getEventDetailsCoaches(this.props.eventsDetails),
      membersOfStaff: getEventDetailsMembersOfStaff(this.props.eventsDetails),
      isChangeFollowingEvents: false,
      editMode: CLUB_EVENT_EDIT_MODE.SINGLE
    };
  }

  componentDidMount() {
    this.loadSelectedStaffDetails();
  }

  getCoaches = (values, text: string) => {
    const coaches = [...this.state.coaches];
    const { user, eventId } = this.props;
    const activeSchoolId = user.activeSchoolId;
    return searchFunctionEventCoaches(user, text, activeSchoolId, coaches, eventId);
  };

  getMembersOfStaff = (values, text: string) => {
    const membersOfStaff = [...this.state.membersOfStaff];
    const { user, eventId } = this.props;
    const activeSchoolId = user.activeSchoolId;
    return searchFunctionEventMembersOfStaff(user, text, activeSchoolId, membersOfStaff, eventId);
  };

  getPermissionId(item): string {
    return typeof item.permissionId !== 'undefined' ? item.permissionId : item.permissions[0].id;
  }

  getStaffKey(item): string {
    return (
      propz.get(item, ['id'], '') ||
      propz.get(item, ['_id'], '') ||
      propz.get(item, ['userId'], '') ||
      propz.get(item, ['permissionId'], '') ||
      getFullName(item)
    );
  }

  getStaffAvatarUrl(item): string {
    const imageSrc =
      propz.get(item, ['profile', 'avatar'], '') ||
      propz.get(item, ['user', 'profile', 'avatar'], '') ||
      propz.get(item, ['userProfile', 'avatar'], '') ||
      propz.get(item, ['profileData', 'avatar'], '') ||
      propz.get(item, ['avatar'], '') ||
      propz.get(item, ['avatarUrl'], '') ||
      propz.get(item, ['photoUrl'], '') ||
      propz.get(item, ['pic'], '') ||
      propz.get(item, ['photo'], '') ||
      propz.get(item, ['picture'], '') ||
      propz.get(item, ['image'], '') ||
      propz.get(item, ['imageUrl'], '');

    return imageSrc && String(imageSrc).startsWith('//') ? `https:${imageSrc}` : imageSrc;
  }

  mergeStaffWithUserDetails(selectedStaff, userDetails) {
    return selectedStaff.map(staff => {
      const staffKey = this.getStaffKey(staff);
      const userDetail = userDetails.find(item => this.getStaffKey(item) === staffKey);

      return userDetail ? { ...userDetail, ...staff, avatar: this.getStaffAvatarUrl(userDetail) } : staff;
    });
  }

  loadSelectedStaffDetails() {
    const { user, eventId } = this.props;
    const { coaches, membersOfStaff } = this.state;
    const coachIds = coaches.map(coach => this.getStaffKey(coach)).filter(Boolean);
    const memberOfStaffIds = membersOfStaff.map(memberOfStaff => this.getStaffKey(memberOfStaff)).filter(Boolean);
    const filter = ids => ({
      where: {
        _id: {
          $in: ids
        }
      },
      limit: ids.length,
      skip: 0
    });

    const requests = [];

    if (coachIds.length > 0) {
      requests.push(
        getSchoolEventCoaches(user, eventId, filter(coachIds)).then(coachesData => ({
          field: 'coaches',
          data: coachesData
        }))
      );
    }

    if (memberOfStaffIds.length > 0) {
      requests.push(
        getSchoolEventStaff(user, eventId, filter(memberOfStaffIds)).then(membersOfStaffData => ({
          field: 'membersOfStaff',
          data: membersOfStaffData
        }))
      );
    }

    if (requests.length === 0) {
      return;
    }

    Promise.all(requests)
      .then(results => {
        const stateUpdate: Partial<State> = {};

        results.forEach(result => {
          if (result.field === 'coaches') {
            stateUpdate.coaches = this.mergeStaffWithUserDetails(this.state.coaches, result.data);
          }

          if (result.field === 'membersOfStaff') {
            stateUpdate.membersOfStaff = this.mergeStaffWithUserDetails(this.state.membersOfStaff, result.data);
          }
        });

        this.setState(stateUpdate as State);
      })
      .catch(error => {
        console.error('Can not load selected event staff details', error);
      });
  }

  getStaffInitials(item): string {
    const fullName = getFullName(item) || '';
    const nameParts = fullName
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (nameParts.length === 0) {
      return '?';
    }

    return nameParts
      .slice(0, 2)
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  onIsChangeFollowingEventsClick = () => {
    this.setState({
      isChangeFollowingEvents: true
    });
  };

  onEditModeClick = editMode => {
    this.setState({
      editMode: editMode
    });
  };

  onSubmit(data, editMode) {
    const coachesData = this.state.coaches.map(coach => {
      const currentCoach = {
        ...coach,
        permissionId: this.getPermissionId(coach),
        staffRole: CLUB_STAFF.COACH,
        firstName: coach.firstName,
        lastName: coach.lastName,
        avatar: this.getStaffAvatarUrl(coach)
      };

      if (typeof coach.userId === 'undefined') {
        propz.set(currentCoach, ['userId'], coach.id);
      }
      return currentCoach;
    });

    const membersOfStaffData = this.state.membersOfStaff.map(memberOfStaff => {
      const currentMemberOfStaff = {
        ...memberOfStaff,
        permissionId: this.getPermissionId(memberOfStaff),
        staffRole: CLUB_STAFF.MEMBER_OF_STAFF,
        firstName: memberOfStaff.firstName,
        lastName: memberOfStaff.lastName,
        avatar: this.getStaffAvatarUrl(memberOfStaff)
      };

      if (typeof memberOfStaff.userId === 'undefined') {
        propz.set(currentMemberOfStaff, ['userId'], memberOfStaff.id);
      }

      return currentMemberOfStaff;
    });

    const { coaches, membersOfStaff, ...rest } = data;
    const dataCopy = {
      ...rest,
      staff: [...coachesData, ...membersOfStaffData]
    };
    this.props.onSubmit(dataCopy, editMode);
  }

  renderStaffChip(staff, selectedStaff, fieldName: 'coaches' | 'membersOfStaff', setFieldValue) {
    const staffKey = this.getStaffKey(staff);
    const staffName = getFullName(staff);
    const imageSrc = this.getStaffAvatarUrl(staff);

    return (
      <div key={staffKey} className="eEntityChipWrap">
        <div className="eEntityChip">
          <div className="eEntityChip__avatar">
            {imageSrc ? (
              <img src={imageSrc} alt={staffName} className="eEntityChip__avatarImg" />
            ) : (
              <div className="eEntityChip__avatarFallback">{this.getStaffInitials(staff)}</div>
            )}
          </div>

          <div className="eEntityChip__name" title={staffName}>
            {staffName}
          </div>

          <button
            type="button"
            className="eEntityChip__remove"
            onClick={() => {
              const selectedStaffFiltered = selectedStaff.filter(item => this.getStaffKey(item) !== staffKey);
              setFieldValue(fieldName, selectedStaffFiltered);
              this.setState({
                [fieldName]: selectedStaffFiltered
              } as any);
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  renderCoaches(coaches, setFieldValue) {
    return coaches.map(coach => {
      return this.renderStaffChip(coach, coaches, 'coaches', setFieldValue);
    });
  }

  renderMembersOfStaff(membersOfStaff, setFieldValue) {
    return membersOfStaff.map(memberOfStaff => {
      return this.renderStaffChip(memberOfStaff, membersOfStaff, 'membersOfStaff', setFieldValue);
    });
  }

  isExistDate = date => {
    return typeof date !== 'undefined';
  };

  renderLabel(label: string): React.ReactNode {
    return <span className="eSchoolEventDetailsForm__label">{label}</span>;
  }

  renderTextInput(label: string, name: string, value: string, placeholder: string, setFieldValue): React.ReactNode {
    return (
      <label className="eSchoolEventDetailsForm__field">
        <span className="eSchoolEventDetailsForm__label">{label}</span>
        <input
          className="eSchoolEventDetailsForm__input"
          type="text"
          value={value || ''}
          placeholder={placeholder}
          onChange={event => setFieldValue(name, event.target.value)}
        />
      </label>
    );
  }

  renderTimePicker(label: string, name: string, value: Date | undefined, setFieldValue): React.ReactNode {
    return (
      <Field name={name}>
        {() => (
          <TimePicker
            label={label}
            value={value || null}
            onChange={time => {
              setFieldValue(name, time);
            }}
            minuteStep={5}
            showConfirm
            clearable
            className="eSchoolEventDetailsForm__timePicker"
          />
        )}
      </Field>
    );
  }

  render() {
    const { eventsDetails, event } = this.props;
    const { coaches, membersOfStaff, isChangeFollowingEvents, editMode } = this.state;

    const defaultDate = new Date();
    defaultDate.setHours(0);
    defaultDate.setMinutes(0);
    const clubInitial = {
      description: propz.get(eventsDetails, ['description'], ''),
      shortDescription: propz.get(eventsDetails, ['shortDescription'], ''),
      kitNotes: propz.get(eventsDetails, ['kitNotes'], ''),
      comments: propz.get(eventsDetails, ['comments'], ''),
      transportDetails: propz.get(eventsDetails, ['transportDetails'], ''),
      oppositionContactDetails: propz.get(eventsDetails, ['oppositionContactDetails'], ''),
      coaches: coaches,
      membersOfStaff: membersOfStaff,
      teamDeparts: this.isExistDate(eventsDetails?.teamDeparts) ? new Date(eventsDetails.teamDeparts) : undefined,
      teamReturns: this.isExistDate(eventsDetails?.teamReturns) ? new Date(eventsDetails.teamReturns) : undefined,
      meetTime: this.isExistDate(eventsDetails?.meetTime) ? new Date(eventsDetails.meetTime) : undefined,
      teaTime: this.isExistDate(eventsDetails?.teaTime) ? new Date(eventsDetails.teaTime) : undefined,
      lunchTime: this.isExistDate(eventsDetails?.lunchTime) ? new Date(eventsDetails.lunchTime) : undefined
    };

    return (
      <Formik
        enableReinitialize
        initialValues={clubInitial}
        onSubmit={values => {
          if (isClubEvent(event) && !isChangeFollowingEvents) {
            this.onIsChangeFollowingEventsClick();
          } else {
            this.onSubmit(values, editMode);
          }
        }}
      >
        {({ setFieldValue, values }) => (
          <Form className="eSchoolEventDetailsForm">
            <div className="eSchoolEventDetailsForm__scroll">
              <div className="eSchoolEventDetailsForm__inner">
                {this.renderTextInput(
                  'Short event description',
                  'shortDescription',
                  values.shortDescription,
                  'Shown on the calendar',
                  setFieldValue
                )}

                <div className="eSchoolEventDetailsForm__field">
                  {this.renderLabel('Event description')}
                  <div className="eSchoolEventDetailsForm__editorRow">
                    <div className="eSchoolEventDetailsInputWith">
                      <HTMLEditorQuillHide
                        user={this.props.user}
                        onTextChange={description => {
                          setFieldValue('description', description);
                        }}
                        value={values.description}
                        isEnabled
                      />
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__field">
                  {this.renderLabel(isClubEvent(event) ? 'Clothing/Kit notes' : 'Kit notes')}
                  <div className="eSchoolEventDetailsForm__editorRow">
                    <div className="eSchoolEventDetailsInputWith">
                      <HTMLEditorQuillHide
                        user={this.props.user}
                        onTextChange={kitNotes => {
                          setFieldValue('kitNotes', kitNotes);
                        }}
                        value={values.kitNotes}
                        isEnabled
                      />
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__field">
                  {this.renderLabel('Comments')}
                  <div className="eSchoolEventDetailsForm__editorRow">
                    <div className="eSchoolEventDetailsInputWith">
                      <HTMLEditorQuillHide
                        user={this.props.user}
                        onTextChange={comments => {
                          setFieldValue('comments', comments);
                        }}
                        value={values.comments}
                        isEnabled
                      />
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__field">
                  {this.renderLabel('Transport')}
                  <div className="eSchoolEventDetailsForm__editorRow">
                    <div className="eSchoolEventDetailsInputWith">
                      <HTMLEditorQuillHide
                        user={this.props.user}
                        onTextChange={transportDetails => {
                          setFieldValue('transportDetails', transportDetails);
                        }}
                        value={values.transportDetails}
                        isEnabled
                      />
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__field">
                  {this.renderLabel('Opposition contact details')}
                  <div className="eSchoolEventDetailsForm__editorRow">
                    <div className="eSchoolEventDetailsInputWith">
                      <HTMLEditorQuillHide
                        user={this.props.user}
                        onTextChange={oppositionContactDetails => {
                          setFieldValue('oppositionContactDetails', oppositionContactDetails);
                        }}
                        value={values.oppositionContactDetails}
                        isEnabled
                      />
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__pair">
                  <div className="eSchoolEventDetailsForm__field">
                    {this.renderLabel(isClubEvent(event) ? 'Leader/Coach' : 'Coach')}
                    <Field name="coaches">
                      {() => (
                        <Autocomplete
                          searchFunction={text => this.getCoaches(values, text)}
                          getElementTitle={getFullName}
                          customClass="eSchoolEventDetailsForm__autocomplete"
                          defaultItem={undefined}
                          onSelect={coach => {
                            const coaches = [].concat(values.coaches, coach);
                            setFieldValue('coaches', coaches);
                            this.setState({ coaches });
                          }}
                        />
                      )}
                    </Field>
                    <div className="eSchoolEventDetailsForm__selectedItems">
                      {this.renderCoaches(values.coaches, setFieldValue)}
                    </div>
                  </div>

                  <div className="eSchoolEventDetailsForm__field">
                    {this.renderLabel('Member of staff')}
                    <Field name="membersOfStaff">
                      {() => (
                        <Autocomplete
                          searchFunction={text => this.getMembersOfStaff(values, text)}
                          getElementTitle={getFullName}
                          customClass="eSchoolEventDetailsForm__autocomplete"
                          defaultItem={undefined}
                          onSelect={memberOfStaff => {
                            const membersOfStaff = [].concat(values.membersOfStaff, memberOfStaff);
                            setFieldValue('membersOfStaff', membersOfStaff);
                            this.setState({ membersOfStaff });
                          }}
                        />
                      )}
                    </Field>
                    <div className="eSchoolEventDetailsForm__selectedItems">
                      {this.renderMembersOfStaff(values.membersOfStaff, setFieldValue)}
                    </div>
                  </div>
                </div>

                <div className="eSchoolEventDetailsForm__pair">
                  {this.renderTimePicker('Meet time', 'meetTime', values.meetTime, setFieldValue)}
                  {this.renderTimePicker('Lunch time', 'lunchTime', values.lunchTime, setFieldValue)}
                </div>

                <div className="eSchoolEventDetailsForm__pair">
                  {this.renderTimePicker(
                    isClubEvent(event) ? 'Group departs' : 'Team departs',
                    'teamDeparts',
                    values.teamDeparts,
                    setFieldValue
                  )}
                  {this.renderTimePicker('Tea time', 'teaTime', values.teaTime, setFieldValue)}
                </div>

                <div className="eSchoolEventDetailsForm__singleTime">
                  {this.renderTimePicker(
                    isClubEvent(event) ? 'Group returns' : 'Team returns',
                    'teamReturns',
                    values.teamReturns,
                    setFieldValue
                  )}
                </div>
              </div>
            </div>

            {isChangeFollowingEvents && (
              <div className="eSchoolEventDetailsForm__editMode">
                <SchoolEventClubEventRadioButtons onEditModeClick={this.onEditModeClick} editMode={editMode} />
              </div>
            )}

            <div className="eSchoolEventDetailsForm__footer">
              <button
                type="button"
                className="eSchoolEventDetailsForm__button eSchoolEventDetailsForm__button--secondary"
                onClick={this.props.onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="eSchoolEventDetailsForm__button eSchoolEventDetailsForm__button--primary"
              >
                Save changes
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}
